import { useEffect, useState, useMemo } from "react";

import { Popover } from "antd";
import debounce from "lodash/debounce";

/**
 * Props for RVPopoverWithTracking.
 */
export type RVPopoverWithTrackingProps = React.ComponentProps<typeof Popover> & {
    /**
     * Stable id used to rebuild the popover key when viewport dimensions change.
     */
    id: string;
};

/**
 * Wraps AntD Popover and forces a remount on viewport resize so placement stays accurate.
 *
 * @param props - Popover props plus a stable id used for resize tracking.
 * @returns A popover that refreshes its internal key when viewport dimensions change.
 * @example
 * <RVPopoverWithTracking id="contact-actions-popover" trigger="click" content="Menu">
 *   <button type="button">Open</button>
 * </RVPopoverWithTracking>
 */
export function RVPopoverWithTracking({ id, ...rest }: RVPopoverWithTrackingProps): JSX.Element {
    const [key, setKey] = useState(id);
    const resizeListener = useMemo(
        () =>
            debounce(
                () => {
                    const width = window.innerWidth;
                    const height = window.innerHeight;
                    setKey(`${id}-${width}-${height}`);
                },
                500,
                { leading: true }
            ),
        [id]
    );

    useEffect(() => {
        setKey(id);
    }, [id]);

    useEffect(() => {
        window.addEventListener("resize", resizeListener);
        return () => {
            window.removeEventListener("resize", resizeListener);
            resizeListener.cancel();
        };
    }, [resizeListener]);

    return <Popover {...rest} key={key} />;
}
